import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { ApiError, apiFetch } from '../../lib/api';

/**
 * 负库存 override 的两主体提权（D-02、Spec §19）。刻意不复用 lib/elevation.tsx
 * 的 `ElevationProvider`——那一层专属密码重输路径（scope 限于 user.manage /
 * override.pin.manage / stockcount.approve），顶部注释明确写着 PIN 双主体路径
 * 留给 M6/M7 接上，这里就是那个接上的地方，不改动既有的密码提权基础设施。
 *
 * 两条路径都调用同一个 `POST /elevation/grant`（method: pin_self / pin_delegated），
 * `intendedRequestBody` 必须与随后真正提交的出库请求体逐字节相同（指纹绑定）。
 */
export function NegativeOverrideModal({
  intendedRequestBody,
  intendedPath = '/inventory/outbound',
  intendedMethod = 'POST',
  onCancel,
  onGranted,
}: {
  readonly intendedRequestBody: unknown;
  /** 令牌绑定的目标端点（sweep #2 M-1）——负库存 override 固定走 POST /inventory/outbound */
  readonly intendedPath?: string;
  readonly intendedMethod?: string;
  readonly onCancel: () => void;
  readonly onGranted: (elevationToken: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'self' | 'delegated'>('self');
  const [pin, setPin] = useState('');
  const [authorizerEmail, setAuthorizerEmail] = useState('');
  const [authorizerPin, setAuthorizerPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { elevationToken } = await apiFetch<{ elevationToken: string }>('/elevation/grant', {
        method: 'POST',
        body:
          mode === 'self'
            ? {
                scope: PERMISSIONS.INVENTORY_OVERRIDE_NEGATIVE,
                method: 'pin_self',
                pin,
                intendedMethod,
                intendedPath,
                intendedRequestBody,
              }
            : {
                scope: PERMISSIONS.INVENTORY_OVERRIDE_NEGATIVE,
                method: 'pin_delegated',
                authorizerEmail,
                authorizerPin,
                intendedMethod,
                intendedPath,
                intendedRequestBody,
              },
      });
      onGranted(elevationToken);
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    mode === 'self' ? pin.length >= 4 : authorizerEmail.trim() !== '' && authorizerPin.length >= 4;

  return (
    <div className="modal-overlay" role="presentation" onClick={onCancel}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('outbound.overrideTitle')}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void submit(e)}
      >
        <h2>{t('outbound.overrideTitle')}</h2>
        <p className="field-hint">{t('outbound.overrideExplain')}</p>

        <div className="row" style={{ gap: 'var(--space-3)', margin: 'var(--space-3) 0' }}>
          <label className="row" style={{ gap: '4px' }}>
            <input type="radio" checked={mode === 'self'} onChange={() => setMode('self')} />
            {t('outbound.overrideSelf')}
          </label>
          <label className="row" style={{ gap: '4px' }}>
            <input
              type="radio"
              checked={mode === 'delegated'}
              onChange={() => setMode('delegated')}
            />
            {t('outbound.overrideDelegated')}
          </label>
        </div>

        {mode === 'self' ? (
          <label className="field">
            <span>{t('elevation.pinLabel')}</span>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              required
            />
          </label>
        ) : (
          <>
            <label className="field">
              <span>{t('elevation.delegatedEmail')}</span>
              <input
                type="email"
                autoFocus
                value={authorizerEmail}
                onChange={(e) => setAuthorizerEmail(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>{t('elevation.delegatedPin')}</span>
              <input
                type="password"
                inputMode="numeric"
                value={authorizerPin}
                onChange={(e) => setAuthorizerPin(e.target.value)}
                required
              />
            </label>
          </>
        )}

        {error && <p className="field-error">{error}</p>}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={submitting}>
            {t('app.cancel')}
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting || !canSubmit}>
            {t('elevation.confirm')}
          </button>
        </div>
      </form>
    </div>
  );
}
